/**
 * @architecture-header 1
 * @layer shared
 * @domain demo
 * @responsibility Project the demo dataset into the module API models used by demo-mode queries.
 * @state-owner none
 * @allowed-imports demo dataset constants and shared view types
 * @forbidden network; React; store implementations
 * @public-api workspace module queries
 * @side-effects none
 * @tests workspace-behaviour.test.ts
 */

import { ACTIVITIES, ACTIVITY_STATUS_LABEL, CLASSES, RUNS } from './demo-data';
import type { ActivityRecord, ClassRecord, RunRecord } from './model';

export interface DemoActivitySummary {
  readonly id: string;
  readonly title: string;
  readonly status: 'parsing' | 'review' | 'ready';
  readonly questions: number;
  readonly needsReview: number;
  readonly updatedLabel: string;
  readonly classIds: string[];
}

export interface DemoClassroomSummary {
  readonly id: string;
  readonly name: string;
  readonly subject: string;
  readonly studentCount: number;
  readonly joinCode: string;
  readonly students: string[];
}

export interface DemoRunSummary {
  readonly id: string;
  readonly activityId: string;
  readonly classId: string;
  readonly mode: 'live' | 'homework';
  readonly status: 'live' | 'open' | 'ended';
}

export function toDemoActivity(activity: ActivityRecord): DemoActivitySummary {
  return {
    id: activity.id,
    title: activity.name,
    status: activity.status === 'failed' ? 'review' : activity.status,
    questions: activity.questionCount,
    needsReview: activity.needsReview ?? activity.reviewed.filter((value) => !value).length,
    updatedLabel: activity.updatedLabel,
    classIds: activity.usedByClassIndexes.map((index) => CLASSES[index].id),
  };
}

export function toDemoClassroom(classroom: ClassRecord): DemoClassroomSummary {
  return {
    id: classroom.id,
    name: classroom.name,
    subject: classroom.subtitle.split(' · ')[0],
    studentCount: classroom.students.length,
    joinCode: classroom.code,
    students: [...classroom.students],
  };
}

export function toDemoRun(run: RunRecord): DemoRunSummary {
  return {
    id: run.id,
    activityId: run.activityId,
    classId: CLASSES[run.classIndex].id,
    mode: run.mode === 'live' ? 'live' : 'homework',
    status: run.status,
  };
}

export const demoActivities: readonly DemoActivitySummary[] = ACTIVITIES.map(toDemoActivity);
export const demoClasses: readonly DemoClassroomSummary[] = CLASSES.map(toDemoClassroom);
export const demoRuns: readonly DemoRunSummary[] = RUNS.map(toDemoRun);

export const demoActivityStatusLabel = ACTIVITY_STATUS_LABEL;
